// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './authentication/authConfig';
import { Image, initializeIcons, mergeStyles, registerIcons } from '@fluentui/react';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';
import { AzureCommunicationErrorBoundary } from './error-telemetry/AzureCommunicationErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import {
  Wand16Regular,
  Call20Regular,
  Dialpad20Regular,
  EmojiSurprise20Regular,
  HeadphonesSoundWave20Regular,
  HeartCircle20Regular,
  Link20Regular,
  MusicNote220Regular,
  Person16Regular,
  PersonFeedback16Regular,
  PersonVoice20Filled,
  PersonVoice20Regular,
  PulseSquareRegular,
  PulseSquare20Regular,
  Run20Regular,
  HandOpenHeart20Regular,
  MicSparkle20Regular,
  Video16Filled,
  Video20Regular,
  BotSparkle20Regular
} from '@fluentui/react-icons';
import { DEFAULT_COMPONENT_ICONS } from '@azure/communication-react';
import acsLogo from './assets/acsLogo.svg';
import teamsLogo from './assets/teamsLogo.svg';

const msalInstance = new PublicClientApplication(msalConfig);

registerIcons({
  icons: {
    ...DEFAULT_COMPONENT_ICONS,
    SignInPerson: <Person16Regular />,
    PersonFeedback: <PersonFeedback16Regular />,
    Experiments: <Wand16Regular />,
    StartCallCamera: <Video20Regular />,
    JoinFromLink: <Link20Regular />,
    SpeakToBot: <BotSparkle20Regular />,
    PSTNOutboundCall: <Call20Regular />,
    MakePSTNCall: <Dialpad20Regular />,
    CallCardCamera: (
      <div className={mergeStyles({ transform: 'scale(0.8)' })}>
        <Video16Filled />
      </div>
    ),
    CallDiagnostics: <PulseSquareRegular />,
    CallDiagnosticsMobileMenu: <PulseSquare20Regular />,
    SoundEffects: <MicSparkle20Regular />,
    NoiseSuppressionOff: <PersonVoice20Regular />,
    NoiseSuppressionOn: <PersonVoice20Filled />,
    PreviewSound: <HeadphonesSoundWave20Regular />,
    ApplauseSound: <HandOpenHeart20Regular />,
    GoSound: <Run20Regular />,
    YesSound: <HeartCircle20Regular />,
    WowSound: <EmojiSurprise20Regular />,
    ElevatorSound: <MusicNote220Regular />,
    AcsLogo: <Image src={acsLogo.toString()} styles={{ image: { height: '1rem', width: '1rem' } }} />,
    TeamsLogoSmall: <Image src={teamsLogo.toString()} styles={{ image: { height: '1rem', width: '1rem' } }} />
  }
});

// Based on this wiki https://github.com/microsoft/fluentui/wiki/Using-icons
// By default, the font-based Fluent UI icons are not added to the bundle or loaded on the page.
// To make the icons available, we may initialize them before the main ReactDOM.render() call.
initializeIcons('https://static2.sharepointonline.com/files/fabric/assets/icons/');
initializeFileTypeIcons();

const domNode = document.getElementById('root');
if (!domNode) {
  throw new Error('Failed to find the root element');
}

createRoot(domNode).render(
  <React.StrictMode>
    <AzureCommunicationErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </AzureCommunicationErrorBoundary>
  </React.StrictMode>
);

serviceWorkerRegistration.register();
